// Librairies
import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

// Interfaces
import { ForecastMeta, ForecastModel, ForecastQualityLevel, ForecastReason } from 'src/app/interfaces/api/chart';

export interface ForecastSummary {
  level: ForecastQualityLevel | null;      // the least reliable series
  wape: number | null;                     // its share of the volume missed on the backtest
  events: number | null;                   // Poisson: the events the rate rests on
  models: ForecastModel[];
  reasons: ForecastReason[];
  outliers: number;
  capped: boolean;
}

const LEVELS: ForecastQualityLevel[] = ['good', 'fair', 'poor'];

// What a forecast says about itself, over all its series; null without forecast.
export function forecastSummary(meta: ForecastMeta | null | undefined): ForecastSummary | null {
  const series = meta?.series ?? [];
  if (!series.length) return null;

  const rated = series.filter(entry => entry.quality?.level);
  const worst = rated.reduce((found, entry) =>
    !found || LEVELS.indexOf(entry.quality!.level!) > LEVELS.indexOf(found.quality!.level!) ? entry : found, rated[0]);

  return {
    level: worst?.quality?.level ?? null,
    wape: worst?.quality?.wape ?? null,
    events: worst?.quality?.events ?? null,
    models: [...new Set(series.map(entry => entry.method).filter((method): method is ForecastModel => !!method))],
    reasons: [...new Set(series.map(entry => entry.reason).filter((reason): reason is ForecastReason => !!reason))],
    outliers: series.reduce((total, entry) => total + (entry.outliers ?? 0), 0),
    capped: series.some(entry => entry.capped),
  };
}

// Under a chart with a forecast: how reliable it proved, the model used, and why a series has none.
@Component({
  selector: 'app-forecast-note',
  standalone: true,
  imports: [CommonModule, MatTooltipModule, TranslateModule],
  templateUrl: './forecast-note.component.html',
  styleUrl: './forecast-note.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class ForecastNoteComponent {
  // Services
  private readonly translate: TranslateService = inject(TranslateService);

  // Variables
  summary: ForecastSummary | null = null;
  // Compact (a text block): the pill alone, the model, the peaks and the cap in its tooltip.
  @Input() compact: boolean = false;
  // Words to put first in the tooltip (a landing's done so far, range and previous period).
  @Input() details: string = '';

  @Input() set meta(meta: ForecastMeta | null | undefined) {
    this.summary = forecastSummary(meta);
  }

  // The pill's tooltip: how it was rated, and in the compact form what the full note says.
  get qualityHint(): string {
    const summary = this.summary;
    if (!summary) return '';

    let quality = '';
    if (this.wapePercent !== null) quality = this.translate.instant('prediction.quality_hint', { wape: this.wapePercent });
    else if (summary.events !== null) quality = this.translate.instant('prediction.quality_hint_events', { events: summary.events });
    const parts = [
      this.details,
      quality,
      ...(this.compact ? [
        ...summary.models.map(model => this.translate.instant('prediction.model_' + model)),
        summary.outliers ? this.translate.instant('editor.forecast_outliers', { count: summary.outliers }) : '',
        summary.capped ? this.translate.instant('prediction.capped') : '',
      ] : []),
    ];
    return parts.filter(Boolean).join(' · ');
  }

  get wapePercent(): number | null {
    return this.summary?.wape === null || this.summary?.wape === undefined ? null : Math.round(this.summary.wape * 100);
  }
}
